// 🔧 silent_auctions.js
// ✅ Purpose: Public-facing manager guide for silent auctions in Let's Go Vote
// ✅ Features:
// - Explains auction item setup, bidding, outbid notifications, and results
// - Helps managers understand how silent auctions can support event fundraising
// - Public and SEO-friendly
// - Uses the Dashboard-style LGV layout
// - Fully block-commented for clarity and future devs

import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../../components/seo_head";

function SilentAuctionsGuide() {
  return (
    <section id="top-content" className="container-fluid py-5">
      <SeoHead
        title="Silent Auctions Guide | Let's Go Vote"
        description="Learn how to create silent auction items, collect bids, notify bidders, and manage auction results with Let's Go Vote."
        path="/guides/silent-auctions"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Guides", path: "/guides" },
          {
            name: "Silent Auctions",
            path: "/guides/silent-auctions",
          },
        ]}
      />
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">🏆 Silent Auctions</h1>
          <p className="lead text-muted">
            Add fundraising opportunities alongside your event experience.
          </p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">
        <section className="mb-5">
          <div className="alert alert-info text-start">
            <h5 className="mb-3">Silent auctions pair naturally with events</h5>
            <p className="mb-0">
              Let&apos;s Go Vote can help managers create auction items, share
              QR codes, collect bids, notify bidders, and review auction
              results.
            </p>
          </div>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 1: Enable auctions for your event</h3>
          <p>
            Silent auctions are optional. When enabled, managers can create
            auction items and display them publicly for attendees to view and
            bid on.
          </p>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 2: Create auction items</h3>
          <p>
            Auction items can include a title, description, photos, starting
            bid, minimum bid increment, and item details. Clear descriptions and
            good photos help bidders understand the value of each item.
          </p>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 3: Use QR codes for bidding</h3>
          <p>
            QR codes can be placed near auction items so attendees can open an
            item directly on their phone and place a bid without searching for
            the item manually.
          </p>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 4: Monitor bids and results</h3>
          <p>
            Managers can review auction activity and results. If outbid
            notifications are enabled, bidders may receive an email when another
            bid passes theirs.
          </p>
        </section>

        <section className="mb-0">
          <div className="alert alert-primary text-start mb-0">
            <h5 className="mb-3">Related guide</h5>
            <p className="mb-3">
              QR codes make auction participation easier during live events.
            </p>

            <Link to="/guides/qr-codes" className="btn btn-primary">
              Review QR Codes
            </Link>
          </div>
        </section>
      </div>
    </section>
  );
}

export default SilentAuctionsGuide;